import { Injectable } from '@angular/core';
import { ExportConstants } from '@ag-common-lib/public-api';
import { Workbook, type Alignment, type Borders, type Fill, type Font, type Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { type ComparisonKpiCard } from './production-comparison-dashboard.models';
import {
  buildComparisonDatasetChartSvgs,
  COMPARISON_RANKING_CHART_WIDTH,
  type ComparisonRanges,
  type ReportingComparisonRankingRowDto,
  type ReportingComparisonRowDto,
  type ReportingComparisonTrendPointDto,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';

export type ProductionComparisonExportColumn = {
  key: string;
  header: string;
  kind: 'text' | 'currency' | 'decimal' | 'percent';
};

export type ProductionComparisonExportTable = {
  title: string;
  columns: ProductionComparisonExportColumn[];
  rows: ReportingComparisonRowDto[];
};

export type ProductionComparisonExportTablePair = {
  increaseTable: ProductionComparisonExportTable;
  decreaseTable: ProductionComparisonExportTable;
};

type ProductionComparisonExportSection = {
  title: string;
  description: string;
  cards: ComparisonKpiCard[];
  overallTable?: ProductionComparisonExportTable;
  moverGridPairs?: ProductionComparisonExportTablePair[];
  charts?: {
    trend: ReportingComparisonTrendPointDto[];
    ranking: ReportingComparisonRankingRowDto[];
    /** The ranges on screen, which name the series the way the page's columns are named. */
    ranges: ComparisonRanges;
  };
};

export type ProductionComparisonExportSheet = {
  sheetName: string;
  title: string;
  subtitle?: string;
  sections: ProductionComparisonExportSection[];
};

@Injectable()
export class ProductionComparisonExportService {
  /** Bold text is wider than the same text is long; a measured width alone came up short. */
  private static readonly boldAllowance = 3;
  private static readonly cardColumnsPerRow = 2;
  private static readonly cardColumnSpan = 5;
  private static readonly cardColumnGap = 1;
  private static readonly trendChartColumnFootprint = 7;
  private static readonly chartColumnGap = 1;
  private static readonly chartHeight = 220;
  /** Charts are drawn at twice their size on the sheet, so they stay sharp when Excel zooms. */
  private static readonly chartPixelRatio = 2;

  async exportWorkbook(args: { fileName: string; sheets: ProductionComparisonExportSheet[] }): Promise<void> {
    const workbook = new Workbook();
    for (const sheet of args.sheets) {
      const worksheet = workbook.addWorksheet(sheet.sheetName, {
        properties: { defaultRowHeight: 20 },
      });
      const sheetColumnCount = this.getSheetColumnCount(sheet.sections);

      this.initializeWorksheet(worksheet, sheet.title, sheet.subtitle ?? 'Comparison export', sheetColumnCount);

      let rowCursor = 4;

      for (const section of sheet.sections) {
        rowCursor = await this.writeSection(worksheet, section, rowCursor, sheetColumnCount);
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${args.fileName}.xlsx`);
  }

  async exportTable(args: {
    fileName: string;
    sheetName: string;
    columns: ProductionComparisonExportColumn[];
    rows: ReportingComparisonRowDto[];
  }): Promise<void> {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(args.sheetName, {
      properties: { defaultRowHeight: 20 },
      views: [{ state: 'frozen', ySplit: 1, showGridLines: true }],
    });
    const table: ProductionComparisonExportTable = {
      title: args.sheetName,
      columns: args.columns,
      rows: args.rows,
    };

    this.applyTableColumnWidths(worksheet, table, 1);

    args.columns.forEach((column, index) => {
      const cell = worksheet.getCell(1, index + 1);
      cell.value = column.header;
      this.styleTableHeaderCell(cell);
    });
    worksheet.getRow(1).height = 24;

    args.rows.forEach((row, rowIndex) => {
      args.columns.forEach((column, columnIndex) => {
        this.writeTableValueCell(
          worksheet.getCell(rowIndex + 2, columnIndex + 1),
          row[column.key as keyof ReportingComparisonRowDto],
          column.kind,
          false,
        );
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${args.fileName}.xlsx`);
  }

  private initializeWorksheet(worksheet: Worksheet, title: string, subtitle: string, sheetColumnCount: number): void {
    worksheet.columns = Array.from({ length: sheetColumnCount }, () => ({ width: 10 }));
    worksheet.views = [{ showGridLines: false }];
    worksheet.mergeCells(`A1:${this.columnName(sheetColumnCount)}1`);
    const titleCell = worksheet.getCell('A1');
    titleCell.value = title;
    titleCell.font = {
      ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
      size: 18,
      bold: true,
      color: { argb: 'FF18324B' },
    };
    titleCell.alignment = this.centerLeftAlignment();
    worksheet.mergeCells(`A2:${this.columnName(sheetColumnCount)}2`);
    const subtitleCell = worksheet.getCell('A2');
    subtitleCell.value = subtitle;
    subtitleCell.font = { ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS, size: 11, color: { argb: 'FF5B6B79' } };
    subtitleCell.alignment = this.centerLeftAlignment();
    subtitleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF6F9FC' },
    };
    worksheet.getRow(1).height = 28;
    worksheet.getRow(2).height = 22;
  }

  private async writeSection(
    worksheet: Worksheet,
    section: ProductionComparisonExportSection,
    startRow: number,
    sheetColumnCount: number,
  ): Promise<number> {
    worksheet.mergeCells(`A${startRow}:${this.columnName(sheetColumnCount)}${startRow}`);
    const titleCell = worksheet.getCell(`A${startRow}`);
    titleCell.value = section.title;
    titleCell.font = {
      ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
      size: 20,
      bold: true,
      color: { argb: 'FF1F3B57' },
    };
    titleCell.alignment = this.centerLeftAlignment();

    worksheet.mergeCells(`A${startRow + 1}:${this.columnName(sheetColumnCount)}${startRow + 1}`);
    const descriptionCell = worksheet.getCell(`A${startRow + 1}`);
    descriptionCell.value = section.description;
    descriptionCell.font = {
      ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
      size: 11,
      color: { argb: 'FF5B6B79' },
    };
    descriptionCell.alignment = this.centerLeftAlignment();

    worksheet.getRow(startRow).height = 28;
    worksheet.getRow(startRow + 1).height = 22;

    const cardsPerRow = Math.min(ProductionComparisonExportService.cardColumnsPerRow, section.cards.length || 1);
    const columnsPerCard = ProductionComparisonExportService.cardColumnSpan;
    const cardColumnGap = ProductionComparisonExportService.cardColumnGap;

    section.cards.forEach((card, index) => {
      const cardRow = startRow + 3 + Math.floor(index / cardsPerRow) * 3;
      const cardOffset = (index % cardsPerRow) * (columnsPerCard + cardColumnGap);
      const startColumn = cardOffset + 1;
      const endColumn = startColumn + columnsPerCard - 1;

      this.writeCard(worksheet, card, cardRow, startColumn, endColumn);
    });

    let rowCursor = startRow + 3 + Math.ceil(section.cards.length / cardsPerRow) * 3 + 1;

    if (section.overallTable) {
      rowCursor = this.writeTable(worksheet, section.overallTable, rowCursor, 1);
    }

    if (section.moverGridPairs?.length) {
      section.moverGridPairs.forEach(moverGridPair => {
        rowCursor = this.writeTablePair(worksheet, moverGridPair, rowCursor);
      });
    }

    if (section.charts) {
      rowCursor = await this.writeChartsSection(worksheet, section.charts, rowCursor);
    }

    return rowCursor;
  }

  /**
   * The dataset's charts, drawn from the same SVG the PDF prints and turned into pictures here.
   *
   * Each is drawn at the size it takes on the sheet, so nothing is stretched to fit a box the
   * columns happened to make.
   */
  private async writeChartsSection(
    worksheet: Worksheet,
    charts: NonNullable<ProductionComparisonExportSection['charts']>,
    startRow: number,
  ): Promise<number> {
    const trendChartWidth = this.getPixelWidthForColumns(
      worksheet,
      1,
      ProductionComparisonExportService.trendChartColumnFootprint,
    );
    const chartHeight = ProductionComparisonExportService.chartHeight;
    const secondChartStartColumn =
      ProductionComparisonExportService.trendChartColumnFootprint +
      ProductionComparisonExportService.chartColumnGap +
      1;

    const svgs = buildComparisonDatasetChartSvgs({ trend: charts.trend, ranking: charts.ranking }, charts.ranges, {
      trendWidth: trendChartWidth,
      trendHeight: chartHeight,
      rankingWidth: COMPARISON_RANKING_CHART_WIDTH,
    });
    const rankingHeight = this.readSvgHeight(svgs.ranking);

    const [premiumTrendImage, policyTrendImage, rankingImage] = await Promise.all([
      this.rasterizeSvg(svgs.premiumTrend, trendChartWidth, chartHeight),
      this.rasterizeSvg(svgs.policyTrend, trendChartWidth, chartHeight),
      this.rasterizeSvg(svgs.ranking, COMPARISON_RANKING_CHART_WIDTH, rankingHeight),
    ]);

    const premiumTrendImageId = worksheet.workbook.addImage({ base64: premiumTrendImage, extension: 'png' });
    const policyTrendImageId = worksheet.workbook.addImage({ base64: policyTrendImage, extension: 'png' });
    const rankingImageId = worksheet.workbook.addImage({ base64: rankingImage, extension: 'png' });

    worksheet.addImage(premiumTrendImageId, {
      tl: { col: 0, row: startRow - 1 },
      ext: { width: trendChartWidth, height: chartHeight },
    });
    worksheet.addImage(policyTrendImageId, {
      tl: { col: secondChartStartColumn - 1, row: startRow - 1 },
      ext: { width: trendChartWidth, height: chartHeight },
    });
    worksheet.addImage(rankingImageId, {
      tl: { col: 0, row: startRow + 13 },
      ext: { width: COMPARISON_RANKING_CHART_WIDTH, height: rankingHeight },
    });

    return startRow + 31;
  }

  /** An SVG as a PNG data URL, by way of an image drawn onto a canvas. */
  private async rasterizeSvg(svg: string, width: number, height: number): Promise<string> {
    const image = new Image();
    const loaded = new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('A chart could not be drawn for the export.'));
    });
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    await loaded;

    const ratio = ProductionComparisonExportService.chartPixelRatio;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const context = canvas.getContext('2d');
    if (!context) {
      return '';
    }

    context.scale(ratio, ratio);
    context.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL('image/png');
  }

  /** The ranking grows with its rows, so its height is read back from the drawing itself. */
  private readSvgHeight(svg: string): number {
    const height = Number(/<svg[^>]*\sheight="(\d+(?:\.\d+)?)"/.exec(svg)?.[1]);

    return Number.isFinite(height) && height > 0 ? height : ProductionComparisonExportService.chartHeight;
  }

  private writeTable(
    worksheet: Worksheet,
    table: ProductionComparisonExportTable,
    startRow: number,
    startColumn: number,
    minimumColumnFootprint = table.columns.length,
  ): number {
    this.applyTableColumnWidths(worksheet, table, startColumn);

    const tableLayout = this.getTableLayout(table, startColumn, minimumColumnFootprint);
    const endColumn = tableLayout.endColumn;
    worksheet.mergeCells(`${this.columnName(startColumn)}${startRow}:${this.columnName(endColumn)}${startRow}`);
    const sectionCell = worksheet.getCell(startRow, startColumn);
    sectionCell.value = table.title;
    sectionCell.font = {
      ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
      size: 13,
      bold: true,
      color: { argb: 'FF1F3B57' },
    };
    sectionCell.alignment = this.centerLeftAlignment();
    worksheet.getRow(startRow).height = 22;

    const headerRow = startRow + 1;

    table.columns.forEach((column, index) => {
      const columnLayout = tableLayout.columns[index];
      const cell = worksheet.getCell(headerRow, columnLayout.startColumn);
      this.mergeTableCellIfNeeded(worksheet, headerRow, columnLayout.startColumn, columnLayout.endColumn);
      cell.value = column.header;
      this.styleTableHeaderCell(cell);
    });

    table.rows.forEach((row, rowIndex) => {
      const worksheetRow = headerRow + 1 + rowIndex;
      table.columns.forEach((column, columnIndex) => {
        const columnLayout = tableLayout.columns[columnIndex];
        const cell = worksheet.getCell(worksheetRow, columnLayout.startColumn);
        this.mergeTableCellIfNeeded(worksheet, worksheetRow, columnLayout.startColumn, columnLayout.endColumn);
        this.writeTableValueCell(
          cell,
          row[column.key as keyof ReportingComparisonRowDto],
          column.kind,
          rowIndex === table.rows.length - 1 && table.rows.length === 1,
        );
      });
    });

    return headerRow + table.rows.length + 2;
  }

  /**
   * A mover pair, one table under the other.
   *
   * They used to sit side by side, which put the decreases out in the far columns and made every
   * sheet twice as wide as the widest thing on it — with both tables padded out to a fixed
   * footprint so the two would line up. Stacked, each one starts in column A and spans exactly
   * the columns the page is showing, so the sheet is one table wide and nothing is padded.
   */
  private writeTablePair(
    worksheet: Worksheet,
    moverGridPair: ProductionComparisonExportTablePair,
    startRow: number,
  ): number {
    const afterIncrease = this.writeTable(worksheet, moverGridPair.increaseTable, startRow, 1);

    return this.writeTable(worksheet, moverGridPair.decreaseTable, afterIncrease, 1);
  }

  private writeCard(
    worksheet: Worksheet,
    card: ComparisonKpiCard,
    row: number,
    startColumn: number,
    endColumn: number,
  ): void {
    const labelCell = worksheet.getCell(row, startColumn);
    const valueCell = worksheet.getCell(row + 1, startColumn);
    const tone = card.tone ?? 'neutral';

    for (let currentColumn = startColumn; currentColumn <= endColumn; currentColumn += 1) {
      worksheet.getColumn(currentColumn).width = Math.max(worksheet.getColumn(currentColumn).width ?? 10, 10);
    }

    this.applyCardBorderFill(worksheet, row, startColumn, endColumn, 1);

    worksheet.mergeCells(`${this.columnName(startColumn)}${row}:${this.columnName(endColumn)}${row}`);
    labelCell.value = card.label;
    labelCell.font = {
      ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
      size: 9,
      bold: true,
      color: { argb: 'FF697A89' },
    };
    labelCell.alignment = this.centerLeftAlignment();

    worksheet.mergeCells(`${this.columnName(startColumn)}${row + 1}:${this.columnName(endColumn)}${row + 1}`);
    valueCell.value = card.value;
    valueCell.font = this.cardValueFont(tone);
    valueCell.alignment = this.centerLeftAlignment();

    worksheet.getRow(row).height = 18;
    worksheet.getRow(row + 1).height = 24;
  }

  private applyCardBorderFill(
    worksheet: Worksheet,
    row: number,
    startColumn: number,
    endColumn: number,
    rowOffset: number,
  ): void {
    for (let currentRow = row; currentRow <= row + rowOffset; currentRow += 1) {
      for (let currentColumn = startColumn; currentColumn <= endColumn; currentColumn += 1) {
        const cell = worksheet.getCell(currentRow, currentColumn);
        cell.fill = this.cardFill();
        cell.border = this.gridBorder('FFD3DEE9');
      }
    }
  }

  private styleTableHeaderCell(cell: {
    font?: Partial<Font>;
    alignment?: Partial<Alignment>;
    fill?: Fill;
    border?: Partial<Borders>;
  }): void {
    cell.font = {
      ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
      size: 10,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center',
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF324B67' },
    };
    cell.border = this.gridBorder('FF324B67');
  }

  private writeTableValueCell(
    cell: {
      value?: unknown;
      font?: Partial<Font>;
      alignment?: Partial<Alignment>;
      fill?: Fill;
      border?: Partial<Borders>;
      numFmt?: string;
    },
    value: unknown,
    kind: ProductionComparisonExportColumn['kind'],
    highlight: boolean,
  ): void {
    cell.value = value == null ? 'N/A' : value;
    cell.font = {
      ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
      size: 10,
      color: { argb: 'FF1F3B57' },
      bold: highlight,
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: kind === 'text' ? 'left' : 'right',
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: highlight ? 'FFEAF2F8' : 'FFFFFFFF' },
    };
    cell.border = this.gridBorder('FFD8E3EC');

    if (typeof value === 'number') {
      if (kind === 'currency') {
        cell.numFmt = '$#,##0.00;[Red]-$#,##0.00';
      }

      if (kind === 'decimal') {
        cell.numFmt = '#,##0.0;[Red]-#,##0.0';
      }

      if (kind === 'percent') {
        cell.numFmt = '0.0%;[Red]-0.0%';
        cell.value = value / 100;
      }

      if (kind === 'currency' || kind === 'decimal' || kind === 'percent') {
        cell.font = {
          ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
          size: 10,
          color: { argb: value < 0 ? 'FFC63F3F' : 'FF1F3B57' },
          bold: highlight,
        };
      }
    }
  }

  private cardFill(): Fill {
    return {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFAFCFE' },
    };
  }

  private cardValueFont(tone: ComparisonKpiCard['tone']): Partial<Font> {
    const colorByTone: Record<NonNullable<ComparisonKpiCard['tone']>, string> = {
      positive: 'FF198754',
      negative: 'FFC63F3F',
      neutral: 'FF1F3B57',
    };

    return {
      ...ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS,
      size: 16,
      bold: true,
      color: { argb: colorByTone[tone ?? 'neutral'] },
    };
  }

  private gridBorder(color: string): Partial<Borders> {
    return {
      top: { style: 'thin', color: { argb: color } },
      left: { style: 'thin', color: { argb: color } },
      bottom: { style: 'thin', color: { argb: color } },
      right: { style: 'thin', color: { argb: color } },
    };
  }

  private centerLeftAlignment(): Partial<Alignment> {
    return {
      vertical: 'middle',
      horizontal: 'left',
    };
  }

  private getTableLayout(
    table: ProductionComparisonExportTable,
    startColumn: number,
    minimumColumnFootprint: number,
  ): {
    endColumn: number;
    columns: Array<{ startColumn: number; endColumn: number }>;
  } {
    const targetColumnCount = Math.max(table.columns.length, minimumColumnFootprint);
    const firstColumnSpan = Math.max(1, targetColumnCount - table.columns.length + 1);
    const columns = table.columns.map((_, index) => {
      if (index === 0) {
        return {
          startColumn,
          endColumn: startColumn + firstColumnSpan - 1,
        };
      }

      const columnStart = startColumn + firstColumnSpan + index - 1;

      return {
        startColumn: columnStart,
        endColumn: columnStart,
      };
    });

    return {
      endColumn: startColumn + targetColumnCount - 1,
      columns,
    };
  }

  private mergeTableCellIfNeeded(worksheet: Worksheet, row: number, startColumn: number, endColumn: number): void {
    if (endColumn <= startColumn) {
      return;
    }

    worksheet.mergeCells(`${this.columnName(startColumn)}${row}:${this.columnName(endColumn)}${row}`);
  }

  private getColumnOffsetForPixels(worksheet: Worksheet, targetPixels: number, startColumn = 1): number {
    let currentPixels = 0;
    let currentColumn = startColumn;

    while (currentPixels < targetPixels) {
      const columnWidth = worksheet.getColumn(currentColumn).width ?? 10;
      const columnPixels = this.columnWidthToPixels(columnWidth);

      if (currentPixels + columnPixels >= targetPixels) {
        return currentColumn - 1 + (targetPixels - currentPixels) / columnPixels;
      }

      currentPixels += columnPixels;
      currentColumn += 1;
    }

    return currentColumn - 1;
  }

  private getPixelWidthForColumns(worksheet: Worksheet, startColumn: number, columnCount: number): number {
    let totalPixels = 0;

    for (let currentColumn = startColumn; currentColumn < startColumn + columnCount; currentColumn += 1) {
      const columnWidth = worksheet.getColumn(currentColumn).width ?? 10;
      totalPixels += this.columnWidthToPixels(columnWidth);
    }

    return totalPixels;
  }

  private columnWidthToPixels(columnWidth: number): number {
    return Math.floor(columnWidth * 7 + 5);
  }

  /**
   * How wide the sheet actually gets written, which is what the title bars are merged across.
   *
   * The cards' own footprint only counts when a sheet has cards: a sheet that is one table
   * would otherwise be ruled for two cards it never writes, and the headings would run past the
   * last column of the table underneath them.
   */
  private getSheetColumnCount(sections: ProductionComparisonExportSection[]): number {
    const hasCards = sections.some(section => section.cards.length);
    const cardColumns = hasCards
      ? ProductionComparisonExportService.cardColumnsPerRow * ProductionComparisonExportService.cardColumnSpan +
        ProductionComparisonExportService.cardColumnGap
      : 1;

    return Math.max(
      cardColumns,
      ...sections.flatMap(section => [
        section.overallTable?.columns.length ?? 0,
        ...(section.moverGridPairs?.map(moverGridPair =>
          Math.max(moverGridPair.increaseTable.columns.length, moverGridPair.decreaseTable.columns.length),
        ) ?? [0]),
      ]),
    );
  }

  /**
   * Wide enough to read, and never narrowed again.
   *
   * Every table on a sheet starts in the same column, so each one asks for its own widths and
   * the last to ask used to win — a mover table of five-figure sums pulled the column back in
   * under the overall row's eight-figure one, which Excel then showed as `#####`. A column only
   * ever grows.
   *
   * The headers and the overall row are bold, which takes more room than the same text laid
   * plain, so what is measured gets an allowance on top rather than being trusted exactly.
   */
  private applyTableColumnWidths(
    worksheet: Worksheet,
    table: ProductionComparisonExportTable,
    startColumn: number,
  ): void {
    table.columns.forEach((column, index) => {
      const columnIndex = startColumn + index;
      const isText = column.kind === 'text';
      const valueWidth = Math.max(
        0,
        ...table.rows.map(row => this.getDisplayWidth(row[column.key as keyof ReportingComparisonRowDto], column.kind)),
      );

      const needed = Math.max(column.header.length, valueWidth) + ProductionComparisonExportService.boldAllowance;
      const bounded = Math.min(isText ? 44 : 22, Math.max(isText ? 14 : 12, needed));
      const current = worksheet.getColumn(columnIndex).width ?? 0;

      worksheet.getColumn(columnIndex).width = Math.max(current, bounded);
    });
  }

  private getDisplayWidth(value: unknown, kind: ProductionComparisonExportColumn['kind']): number {
    if (value == null) {
      return 3;
    }

    if (typeof value !== 'number') {
      return String(value).length;
    }

    if (kind === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value).length;
    }

    if (kind === 'percent') {
      return `${value.toFixed(1)}%`.length;
    }

    if (kind === 'decimal') {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).length;
    }

    return value.toLocaleString('en-US').length;
  }

  private columnName(columnNumber: number): string {
    let dividend = columnNumber;
    let columnName = '';

    while (dividend > 0) {
      const modulo = (dividend - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      dividend = Math.floor((dividend - modulo) / 26);
    }

    return columnName;
  }
}
